// Custom hook for authentication state and actions
import { useState, useEffect, useCallback } from 'react';
import {
    signInAnonymously,
    onAuthStateChanged,
    signInWithPopup,
    linkWithPopup,
    signOut
} from 'firebase/auth';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    serverTimestamp,
    setDoc,
    writeBatch
} from 'firebase/firestore';
import { auth, googleProvider, db, appId } from '../utils/firebase';
import { getTodayDateString } from '../utils/helpers';

const MIGRATED_COLLECTIONS = ['tarotReadings', 'birthCharts', 'synastryReadings'];
const LINK_CONFLICT_CODES = [
    'auth/account-exists-with-different-credential',
    'auth/credential-already-in-use',
    'auth/email-already-in-use'
];

const getUserCollectionPath = (uid, collectionName) =>
    `artifacts/${appId}/users/${uid}/${collectionName}`;

const snapshotAnonymousData = async (uid) => {
    const userDocRef = doc(db, 'users', uid);
    const userDocSnap = await getDoc(userDocRef);
    const collections = {};

    await Promise.all(MIGRATED_COLLECTIONS.map(async (collectionName) => {
        const snapshot = await getDocs(collection(db, getUserCollectionPath(uid, collectionName)));
        collections[collectionName] = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            data: docSnap.data()
        }));
    }));

    return {
        sourceUid: uid,
        userData: userDocSnap.exists() ? userDocSnap.data() : null,
        collections
    };
};

const commitIfNeeded = async (batch, hasWrites) => {
    if (hasWrites.value) {
        await batch.commit();
        hasWrites.value = false;
    }
};

const migrateAnonymousData = async (snapshot, targetUid) => {
    if (!snapshot || snapshot.sourceUid === targetUid) {
        return { copied: 0 };
    }

    let copied = 0;
    let batch = writeBatch(db);
    let writesInBatch = { value: false };

    for (const collectionName of MIGRATED_COLLECTIONS) {
        const docsToCopy = snapshot.collections[collectionName] || [];

        for (const sourceDoc of docsToCopy) {
            const targetRef = doc(db, getUserCollectionPath(targetUid, collectionName), sourceDoc.id);
            const targetSnap = await getDoc(targetRef);

            if (!targetSnap.exists()) {
                batch.set(targetRef, {
                    ...sourceDoc.data,
                    userId: targetUid,
                    migratedFromAnonymousUid: snapshot.sourceUid,
                    migratedAt: serverTimestamp()
                });
                copied += 1;
                writesInBatch.value = true;

                if (copied % 400 === 0) {
                    await commitIfNeeded(batch, writesInBatch);
                    batch = writeBatch(db);
                }
            }
        }
    }

    const anonymousDailyGuidance = snapshot.userData?.dailyGuidance;
    if (anonymousDailyGuidance) {
        const targetUserRef = doc(db, 'users', targetUid);
        const targetUserSnap = await getDoc(targetUserRef);
        const targetDailyGuidance = targetUserSnap.exists()
            ? targetUserSnap.data().dailyGuidance
            : null;

        if (!targetDailyGuidance) {
            batch.set(targetUserRef, { dailyGuidance: anonymousDailyGuidance }, { merge: true });
            writesInBatch.value = true;
        }
    }

    await commitIfNeeded(batch, writesInBatch);
    return { copied };
};

const ensureGoogleUserDoc = async (user, fallbackDailyGuidance = null) => {
    const userDocRef = doc(db, 'users', user.uid);
    const userDocSnap = await getDoc(userDocRef);
    const existingData = userDocSnap.exists() ? userDocSnap.data() : {};
    const nextProfile = {
        uid: user.uid,
        displayName: user.displayName || existingData.displayName || 'Cosmic Seeker',
        email: user.email || existingData.email || null,
        photoURL: user.photoURL || existingData.photoURL || null,
        provider: 'google.com',
        updatedAt: serverTimestamp()
    };

    if (!userDocSnap.exists()) {
        nextProfile.createdAt = serverTimestamp();
        nextProfile.dailyGuidance = fallbackDailyGuidance || null;
    } else if (!existingData.dailyGuidance && fallbackDailyGuidance) {
        nextProfile.dailyGuidance = fallbackDailyGuidance;
    }

    await setDoc(userDocRef, nextProfile, { merge: true });
};

export const useAuth = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [dailyCardData, setDailyCardData] = useState(null);
    const [authError, setAuthError] = useState('');

    // Load daily card for authenticated users
    const loadUserDailyCard = useCallback(async (uid) => {
        if (!uid) return;
        try {
            const userDocRef = doc(db, "users", uid);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                if (userData.dailyGuidance && userData.dailyGuidance.dateDrawn === getTodayDateString()) {
                    setDailyCardData(userData.dailyGuidance);
                } else {
                    setDailyCardData(null);
                }
            } else {
                setDailyCardData(null);
            }
        } catch (error) {
            console.error("Error loading daily card:", error);
            setDailyCardData(null);
        }
    }, []);

    // Auth state listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setCurrentUser({
                    uid: user.uid,
                    isAnonymous: user.isAnonymous,
                    displayName: user.displayName,
                    email: user.email,
                    photoURL: user.photoURL
                });
                if (!user.isAnonymous) {
                    loadUserDailyCard(user.uid);
                } else {
                    setDailyCardData(null);
                }
            } else {
                // Sign in anonymously if no user
                try {
                    await signInAnonymously(auth);
                } catch (authError) {
                    console.error("Error signing in anonymously:", authError);
                    setAuthError("Authentication failed. Please refresh the page.");
                    setCurrentUser(null);
                    setDailyCardData(null);
                }
            }
            setIsAuthReady(true);
        });
        return () => unsubscribe();
    }, [loadUserDailyCard]);

    // Google login
    const handleGoogleLogin = useCallback(async () => {
        try {
            setAuthError('');
            const anonymousUser = auth.currentUser?.isAnonymous ? auth.currentUser : null;

            if (anonymousUser) {
                try {
                    const linkedResult = await linkWithPopup(anonymousUser, googleProvider);
                    await ensureGoogleUserDoc(linkedResult.user);
                    return { success: true, linked: true };
                } catch (linkError) {
                    if (!LINK_CONFLICT_CODES.includes(linkError.code)) {
                        throw linkError;
                    }

                    const anonymousSnapshot = await snapshotAnonymousData(anonymousUser.uid);
                    const signInResult = await signInWithPopup(auth, googleProvider);
                    await ensureGoogleUserDoc(
                        signInResult.user,
                        anonymousSnapshot.userData?.dailyGuidance || null
                    );
                    const migration = await migrateAnonymousData(anonymousSnapshot, signInResult.user.uid);
                    return { success: true, linked: false, migrated: true, copied: migration.copied };
                }
            }

            const result = await signInWithPopup(auth, googleProvider);
            await ensureGoogleUserDoc(result.user);
            return { success: true };
        } catch (error) {
            console.error("Google Sign-In Error:", error);
            if (error.code === 'auth/popup-closed-by-user') {
                setAuthError('Google Sign-In cancelled by user.');
            } else if (error.code === 'auth/network-request-failed') {
                setAuthError('Network error during Google Sign-In. Please check your connection.');
            } else {
                setAuthError(`Google Sign-In Failed: ${error.message}`);
            }
            return { success: false, error: error.message };
        }
    }, []);

    // Logout
    const handleLogout = useCallback(async () => {
        try {
            setAuthError('');
            await signOut(auth);
            setDailyCardData(null);
            return { success: true };
        } catch (error) {
            console.error("Logout Error:", error);
            setAuthError(`Logout Failed: ${error.message}`);
            return { success: false, error: error.message };
        }
    }, []);

    return {
        currentUser,
        isAuthReady,
        dailyCardData,
        setDailyCardData,
        authError,
        setAuthError,
        handleGoogleLogin,
        handleLogout,
        loadUserDailyCard
    };
};
